import React, { useState , useEffect, useRef, useCallback, useMemo } from 'react';
import { API } from "aws-amplify";
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, naturalSortCompare, useDebouncedValue } from '../../../js/Helpers';
import { getPeriodNameFromCoursePeriodId } from '../../../js/PeriodHelpers';
import { LABEL_COLOR_OPTIONS, DEFAULT_LABEL_COLOR, getLabelColorClass, isInclusionLabel } from '../utils/labelColorUtils';
import LabelColorPicker from '../common/LabelColorPicker';

import SortToggle from '../../SortToggle';
import '../../../css/hsms/edit_modals.css';
import Portal from '../../Portal';
import StudentLabelAssignments from '../reports/student_label_assignments';
import EditSection from '../modals/edit_section';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faChevronUp, faCheckSquare, faMinusSquare, faSortDown, faTrashAlt, faTimes, faPlus, faCheck, faUserGraduate, faEdit } from '@fortawesome/free-solid-svg-icons';
import { faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';

export default function DatabaseDataLabels({ schoolInfo, scheduleInfo, scheduleStructureData, labels, setLabels, sections, setSections, students, setStudents, courses, setCourses, teachers, setTeachers, classrooms, subsections, setSubsections, lunchInfo, sectionsLoading, studentsLoading, coursesLoading, teachersLoading, classroomsLoading = false }) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);

	const [toggledLabels, setToggledLabels] = useState([]);
	const [removingSectionKeys, setRemovingSectionKeys] = useState([]);
	const [selectedStudentsByLabel, setSelectedStudentsByLabel] = useState({});
	const [removingStudentsByLabel, setRemovingStudentsByLabel] = useState([]);
	const [bulkRemoveStudentsModalData, setBulkRemoveStudentsModalData] = useState(null);
	const [isAddingLabel, setIsAddingLabel] = useState(false);
	const [newLabelName, setNewLabelName] = useState('');
	const [newLabelColor, setNewLabelColor] = useState(DEFAULT_LABEL_COLOR);
	const [isSavingNewLabel, setIsSavingNewLabel] = useState(false);
	const [editingLabelID, setEditingLabelID] = useState(null);
	const [editingLabelName, setEditingLabelName] = useState('');
	const [isSavingEditedLabelName, setIsSavingEditedLabelName] = useState(false);
	const [selectedData, setSelectedData] = useState([]);
	const [isDeletingSelected, setIsDeletingSelected] = useState(false);
	const [deletingLabelIDs, setDeletingLabelIDs] = useState([]);
	const [selectedLabelID, setSelectedLabelID] = useState(null);
	const [showEditSectionModal, setShowEditSectionModal] = useState(false);
	const [currentSectionID, setCurrentSectionID] = useState(null);

	const filteredResultsRef = useRef(filteredResults);
	filteredResultsRef.current = filteredResults;

	const toggleEditSection = useCallback((section_id = null) => {
		if(section_id)
		{
			let edit_section_id = section_id;
			const subsection_info = (Array.isArray(subsections) ? subsections : []).find(subsection => `${subsection.subsection_id}` === `${section_id}`);
			if(subsection_info && subsection_info.section_id) edit_section_id = subsection_info.section_id;
			else if(subsection_info && subsection_info.section) edit_section_id = subsection_info.section;

			setCurrentSectionID(edit_section_id);
			setShowEditSectionModal(true);
			return;
		}

		setShowEditSectionModal((previous_showing) => !previous_showing);
	}, [subsections]);

	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsSearching(false);
		}
	}

	const toggleLabel = (label_id) => {
		const label_index = toggledLabels.findIndex(label => `${label}` === `${label_id}`);

		if(label_index !== -1)
		{
			toggledLabels.splice(label_index, 1);
		}
		else
		{
			toggledLabels.push(label_id);
		}

		setToggledLabels([...toggledLabels]);
	}

	const toggleSelectData = (label_id) => {
		const label_id_key = `${label_id}`;
		setSelectedData((previous_selected) => {
			const next_selected = Array.isArray(previous_selected) ? [...previous_selected] : [];
			const selected_index = next_selected.findIndex(selected_id => `${selected_id}` === label_id_key);

			if(selected_index !== -1) next_selected.splice(selected_index, 1);
			else next_selected.push(label_id_key);

			return next_selected;
		});
	}

	const toggleSelectAll = () => {
		const any_data_selected = selectedData.length > 0;

		if(any_data_selected)
		{
			setSelectedData([]);
			return;
		}

		const all_selected_data = displayResults.reduce((results, label_info) => {
			results.push(`${label_info.label_id}`);
			return results;
		}, []);
		setSelectedData(all_selected_data);
	}

	const toggleSelectStudent = (label_id, student_id) => {
		const label_key = `${label_id}`;
		const student_key = `${student_id}`;

		setSelectedStudentsByLabel((prev_selected) => {
			const selected_students = Array.isArray(prev_selected[label_key]) ? [...prev_selected[label_key]] : [];
			const selected_student_index = selected_students.findIndex(selected_id => `${selected_id}` === student_key);

			if(selected_student_index !== -1)
			{
				selected_students.splice(selected_student_index, 1);
			}
			else
			{
				selected_students.push(student_key);
			}

			return {...prev_selected, [label_key]: selected_students};
		});
	}

	const toggleSelectAllStudents = (label_id, students_with_label) => {
		const label_key = `${label_id}`;
		const all_student_ids = students_with_label.map(student_info => `${student_info.student_id}`);

		setSelectedStudentsByLabel((prev_selected) => {
			const selected_students = Array.isArray(prev_selected[label_key]) ? prev_selected[label_key] : [];
			const has_selected_students = selected_students.length > 0;

			return {...prev_selected, [label_key]: has_selected_students ? [] : all_student_ids};
		});
	}

	const openBulkRemoveStudentsModal = (label_id, label_name, students_with_label) => {
		const label_key = `${label_id}`;
		const selected_students = Array.isArray(selectedStudentsByLabel[label_key]) ? selectedStudentsByLabel[label_key] : [];
		if(selected_students.length === 0) return;

		const selected_student_infos = students_with_label.filter(student_info => selected_students.includes(`${student_info.student_id}`));
		if(selected_student_infos.length === 0) return;

		setBulkRemoveStudentsModalData({
			label_id: label_key,
			label_name: label_name,
			students: selected_student_infos
		});
	}

	const removeSelectedStudentsFromLabel = async () => {
		if(!bulkRemoveStudentsModalData || !setStudents || !schoolInfo || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id) return;

		const label_id = `${bulkRemoveStudentsModalData.label_id}`;
		const selected_student_ids = bulkRemoveStudentsModalData.students.map(student_info => `${student_info.student_id}`);
		if(selected_student_ids.length === 0) return;

		setBulkRemoveStudentsModalData(null);
		setRemovingStudentsByLabel(prev_labels => prev_labels.includes(label_id) ? prev_labels : [...prev_labels, label_id]);

		setStudents((previous_students) => {
			const previous_students_array = Array.isArray(previous_students) ? previous_students : [];

			return previous_students_array.map((student_info) => {
				const student_id = `${student_info.student_id}`;
				if(!selected_student_ids.includes(student_id)) return student_info;

				const student_labels = Array.isArray(student_info.student_labels) ? [...student_info.student_labels] : [];
				const label_index = student_labels.findIndex(student_label => student_label && `${student_label.label_id}` === label_id);
				if(label_index !== -1) student_labels.splice(label_index, 1);

				return {...student_info, student_labels: student_labels};
			});
		});

		setSelectedStudentsByLabel((previous_selected) => {
			return {...previous_selected, [label_id]: []};
		});

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-student-label';

		try {
			await Promise.all(selected_student_ids.map((student_id) => {
				const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'remove', student_id:student_id, label_id:label_id};
				const myInit = {
					response: true,
					body: JSON.stringify(data),
				};

				return API.post(apiName, url, myInit);
			}));
		} catch(e)
		{
			console.log(e.response);
		} finally
		{
			setRemovingStudentsByLabel(prev_labels => prev_labels.filter(prev_label => prev_label !== label_id));
		}
	}

	const removeSectionFromLabel = async (section_id, label_id) => {
		if(!setSections || !schoolInfo || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id) return;

		const key = `${label_id}-${section_id}`;
		setRemovingSectionKeys(prev_keys => prev_keys.includes(key) ? prev_keys : [...prev_keys, key]);

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'remove', section_id:section_id, label_id:label_id};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-section-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			await API.post(apiName, url, myInit);

			const source_sections = Array.isArray(sections) ? sections : [];
			const target_section = source_sections.find(section => `${section.section_id}` === `${section_id}`);
			const target_span_id = target_section ? target_section.span_id : null;

			setSections((previous_sections) => {
				const sections_to_update = Array.isArray(previous_sections) ? previous_sections : [];

				return sections_to_update.map((section_info) => {
					const is_target_section = `${section_info.section_id}` === `${section_id}`;
					const is_target_span_section = (target_span_id && `${section_info.span_id}` === `${target_span_id}` && `${section_info.section_id}` !== `${section_id}`);
					if(!is_target_section && !is_target_span_section) return section_info;

					const section_labels = Array.isArray(section_info.labels) ? [...section_info.labels] : [];
					const label_index = section_labels.findIndex(temp_label_id => `${temp_label_id}` === `${label_id}`);
					if(label_index !== -1) section_labels.splice(label_index, 1);

					return {...section_info, labels: section_labels};
				});
			});
		} catch(e)
		{
			console.log(e.response);
		} finally
		{
			setRemovingSectionKeys(prev_keys => prev_keys.filter(prev_key => prev_key !== key));
		}
	}

	const AddSectionModalContent = ({ label_id, label_name }) => {
		const [search_value, setSearchValue] = useState('');
		const [adding_section_ids, setAddingSectionIDs] = useState([]);
		const [added_section_ids, setAddedSectionIDs] = useState([]);

		const section_options = useMemo(() => {
			const section_data = Array.isArray(sections) ? sections : [];
			const course_data = Array.isArray(courses) ? courses : [];
			const teacher_data = Array.isArray(teachers) ? teachers : [];
			const subsection_data = Array.isArray(subsections) ? subsections : [];

			const subsection_ids = new Set(subsection_data.map((subsection_info) => `${subsection_info.subsection_id}`));
			const logged_span_ids = new Set();

			const rows = section_data.reduce((results, section_info) => {
				if(!section_info || `${section_info.section_type}` !== '1') return results;

				const section_id_key = `${section_info.section_id}`;
				const is_subsection = subsection_ids.has(section_id_key);
				const span_id = section_info.span_id ? `${section_info.span_id}` : null;

				if(span_id && !is_subsection)
				{
					if(logged_span_ids.has(span_id)) return results;
					logged_span_ids.add(span_id);
				}

				const course_info = course_data.find(course => `${course.course_id}` === `${section_info.course_id}`);
				const teacher_info = teacher_data.find(teacher => `${teacher.teacher_id}` === `${section_info.teacher_id}`);
				const section_number = section_info.section_number ? section_info.section_number : '--';
				const course_name = course_info ? course_info.name : 'Unknown Course';
				const course_code = course_info ? course_info.course_code : '--';
				const teacher_name = teacher_info ? `${teacher_info.name}, ${teacher_info.first_name}` : 'No Teacher Assigned';
				const num_students = Array.isArray(section_info.student_list) ? section_info.student_list.length : 0;
				const section_suffix = is_subsection ? ' • Subsection' : '';
				let course_periods = Array.isArray(section_info.course_periods) ? [...section_info.course_periods] : [];

				if(span_id)
				{
					for(const spanned_section of section_data)
					{
						if(`${spanned_section.span_id || ''}` !== span_id) continue;

						const spanned_section_periods = Array.isArray(spanned_section.course_periods) ? spanned_section.course_periods : [];
						if(spanned_section_periods.length === 0) continue;

						course_periods = [...new Set([...course_periods, ...spanned_section_periods])];
					}
				}

				const period_names = [];
				course_periods.forEach((course_period_id) => {
					const period_name = getPeriodNameFromCoursePeriodId(scheduleStructureData, course_period_id, {
						schedule_type: scheduleInfo?.schedule_type,
						current_day: '1',
					});
					if(!period_name) return;
					if(!period_names.includes(period_name)) period_names.push(period_name);
				});
				period_names.sort((a, b) => `${a}`.localeCompare(`${b}`));

				results.push({
					section_id: section_info.section_id,
					section_id_key: section_id_key,
					labels: Array.isArray(section_info.labels) ? section_info.labels : [],
					course_text: `${course_code} - ${course_name}${section_suffix}`,
					teacher_name: teacher_name,
					period_names: period_names,
					num_students: num_students,
					search_text: `${course_name} ${course_code} ${teacher_name} section ${section_number}`.toLowerCase(),
				});

				return results;
			}, []);

			rows.sort((a, b) => `${a.course_text}`.localeCompare(`${b.course_text}`));

			const trimmed_search_value = `${search_value}`.trim().toLowerCase();
			if(trimmed_search_value.length === 0) return rows;

			return rows.filter(section_info => section_info.search_text.includes(trimmed_search_value));
		}, [sections, courses, teachers, subsections, scheduleInfo, scheduleStructureData, search_value]);

		const addSectionToLabelFromModal = async (section_id) => {
			if(!setSections || !schoolInfo || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id) return;

			const section_id_key = `${section_id}`;
			const label_id_key = `${label_id}`;
			if(adding_section_ids.includes(section_id_key)) return;

			setAddingSectionIDs((previous_ids) => [...previous_ids, section_id_key]);

			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'add', section_id:section_id, label_id:label_id_key};
			const apiName = process.env.REACT_APP_ENDPOINT_NAME;
			const url = '/admin/update-section-label';
			const myInit = {
				response: true,
				body: JSON.stringify(data),
			};

			try {
				await API.post(apiName, url, myInit);

				setSections((previous_sections) => {
					const sections_to_update = Array.isArray(previous_sections) ? previous_sections : [];
					const target_section = sections_to_update.find(section => `${section.section_id}` === section_id_key);
					const target_span_id = target_section ? target_section.span_id : null;

					return sections_to_update.map((section_info) => {
						const is_target_section = `${section_info.section_id}` === section_id_key;
						const is_target_span_section = (target_span_id && `${section_info.span_id}` === `${target_span_id}` && `${section_info.section_id}` !== section_id_key);
						if(!is_target_section && !is_target_span_section) return section_info;

						const section_labels = Array.isArray(section_info.labels) ? [...section_info.labels] : [];
						const label_index = section_labels.findIndex(temp_label_id => `${temp_label_id}` === label_id_key);
						if(label_index === -1) section_labels.push(label_id_key);

						return {...section_info, labels: section_labels};
					});
				});

				setAddedSectionIDs((previous_ids) => previous_ids.includes(section_id_key) ? previous_ids : [...previous_ids, section_id_key]);
			} catch(e)
			{
				console.log(e.response);
			} finally
			{
				setAddingSectionIDs((previous_ids) => previous_ids.filter(id => id !== section_id_key));
			}
		}

		return (
			<div className='text-align-left'>
				<h1 style={{margin:'0px'}}>Add Section</h1>
				<p style={{margin:'10px 0px 20px 0px'}}>Which section would you like to add to <strong>{label_name || '(No Name)'}</strong>?</p>
				<div className='search-add-adding-input-container' style={{marginBottom:'10px'}}>
					<input className='search-add-adding-input' value={search_value} onChange={(e) => setSearchValue(e.target.value)} placeholder='Search for a section by course code, course name, or teacher'/>
					<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
				</div>
				<div className='edit-section-move-student-section-row' style={{backgroundColor:'#fff'}}>
					<div><strong><u>Course</u></strong></div>
					<div><strong><u>Periods</u></strong></div>
					<div className='white-text'>.</div>
					<div className='white-text'>.</div>
				</div>
				{section_options.length === 0 &&
					<div className='upload-screen-empty-requests' style={{margin:'18px 0px'}}>No matching sections found.</div>
				}
				{section_options.map((section_info, index) => {
					const section_has_label = section_info.labels.findIndex(section_label_id => `${section_label_id}` === `${label_id}`) !== -1 || added_section_ids.includes(section_info.section_id_key);
					const section_adding = adding_section_ids.includes(section_info.section_id_key);

					return (
						<div key={`${section_info.section_id_key}-${index}`} className='edit-section-move-student-section-row'>
							<div>
								<div>{section_info.course_text}</div>
								<div className='dark-gray-text'>{section_info.teacher_name}</div>
							</div>
							<div>
								{(section_info.period_names || []).length === 0 ?
									(
										<>--</>
									):
									(
										<>
										{section_info.period_names.map((period_name, period_index) => (
											<div key={`${section_info.section_id_key}-${period_index}`} className='badge badge-secondary'>{period_name}</div>
										))}
										</>
									)
								}
							</div>
							<div className='mb-section-students text-align-center'><FontAwesomeIcon icon={faUserGraduate}/> {section_info.num_students}</div>
							{section_has_label ?
								(
									<div className='small-text align-right'>Added</div>
								): section_adding ?
								(
									<div className='align-right'>
										<img src={require('../../../images/balls.gif')} alt='adding...' style={{height:'26px'}}/>
									</div>
								):
								(
									<div className='btn-small btn-round blue-btn align-right' style={{maxWidth:'70px'}} onClick={() => addSectionToLabelFromModal(section_info.section_id)}>Select</div>
								)
							}
						</div>
					);
				})}
				<div style={{display:'flex', justifyContent:'flex-end', marginTop:'20px'}}>
					<div className='btn btn-small gray-btn' onClick={() => swal.close()}>Done</div>
				</div>
			</div>
		);
	}

	const openAddSectionModal = async (label_id, label_name) => {
		const options =  {
			button: false,
			className: 'school-data-labels-add-section-modal',
			content: (
				<AddSectionModalContent label_id={`${label_id}`} label_name={label_name}/>
			)
		}

		await swal(options);
	}

	const updateLabelColor = async (label_id, tag_color) => {
		if(!setLabels || !schoolInfo || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id) return;

		const label_id_key = `${label_id}`;
		const label_info = (Array.isArray(labels) ? labels : []).find(label => `${label.label_id}` === label_id_key);
		if(!label_info) return;

		const current_tag_color = getLabelColorClass(label_info);
		if(current_tag_color === tag_color) return;

		setLabels((previous_labels) => {
			const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
			const target_label_index = next_labels.findIndex(label => `${label.label_id}` === label_id_key);
			if(target_label_index !== -1) next_labels[target_label_index] = {...next_labels[target_label_index], tag_color: tag_color};
			return next_labels;
		});

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'update_color', label_id:label_id, label:label_info.label, tag_color:tag_color};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const saved_tag_color = (response.data && response.data.tag_color) ? response.data.tag_color : tag_color;
			setLabels((previous_labels) => {
				const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
				const target_label_index = next_labels.findIndex(label => `${label.label_id}` === label_id_key);
				if(target_label_index !== -1) next_labels[target_label_index] = {...next_labels[target_label_index], tag_color: saved_tag_color};
				return next_labels;
			});
		} catch(e)
		{
			setLabels((previous_labels) => {
				const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
				const target_label_index = next_labels.findIndex(label => `${label.label_id}` === label_id_key);
				if(target_label_index !== -1) next_labels[target_label_index] = {...next_labels[target_label_index], tag_color: current_tag_color};
				return next_labels;
			});
			console.log(e.response);
		}
	}

	const startAddingLabel = () => {
		setEditingLabelID(null);
		setEditingLabelName('');
		setIsAddingLabel(true);
		setNewLabelName('');
		setNewLabelColor(DEFAULT_LABEL_COLOR);
	}

	const cancelAddingLabel = () => {
		if(isSavingNewLabel) return;
		setIsAddingLabel(false);
		setNewLabelName('');
		setNewLabelColor(DEFAULT_LABEL_COLOR);
	}

	const startEditingLabelName = (label_info) => {
		if(!label_info || !('label_id' in label_info)) return;

		setIsAddingLabel(false);
		setEditingLabelID(`${label_info.label_id}`);
		setEditingLabelName(label_info.label ? label_info.label : '');
	}

	const cancelEditingLabelName = () => {
		if(isSavingEditedLabelName) return;
		setEditingLabelID(null);
		setEditingLabelName('');
	}

	useEffect(() => {
		if(!editingLabelID || isSavingEditedLabelName) return;

		const handlePointerOutsideLabelEditor = (event) => {
			const target = event.target;
			if(target?.closest?.('.school-data-label-name-editing')) return;

			cancelEditingLabelName();
		};

		document.addEventListener('mousedown', handlePointerOutsideLabelEditor);
		document.addEventListener('touchstart', handlePointerOutsideLabelEditor);

		return () => {
			document.removeEventListener('mousedown', handlePointerOutsideLabelEditor);
			document.removeEventListener('touchstart', handlePointerOutsideLabelEditor);
		};
	}, [editingLabelID, isSavingEditedLabelName]);

	const saveEditedLabelName = async (label_id) => {
		if(isSavingEditedLabelName) return;
		if(!schoolInfo || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id || !setLabels) return;

		const label_id_key = `${label_id}`;
		const trimmed_label_name = `${editingLabelName}`.trim();
		if(!trimmed_label_name) return;

		const current_label_info = (Array.isArray(labels) ? labels : []).find(label => `${label.label_id}` === label_id_key);
		if(!current_label_info) return;

		const previous_label_name = current_label_info.label ? current_label_info.label : '';
		if(trimmed_label_name === previous_label_name)
		{
			setEditingLabelID(null);
			setEditingLabelName('');
			return;
		}

		setIsSavingEditedLabelName(true);
		setLabels((previous_labels) => {
			const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
			const current_label_index = next_labels.findIndex(label => `${label.label_id}` === label_id_key);
			if(current_label_index !== -1) next_labels[current_label_index] = {...next_labels[current_label_index], label: trimmed_label_name};
			return next_labels;
		});

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'update', label_id:label_id_key, label:trimmed_label_name, tag_color:getLabelColorClass(current_label_info)};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const saved_tag_color = (response.data && response.data.tag_color) ? response.data.tag_color : getLabelColorClass(current_label_info);

			setLabels((previous_labels) => {
				const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
				const current_label_index = next_labels.findIndex(label => `${label.label_id}` === label_id_key);
				if(current_label_index !== -1) next_labels[current_label_index] = {...next_labels[current_label_index], label: trimmed_label_name, tag_color: saved_tag_color};
				return next_labels;
			});

			setEditingLabelID(null);
			setEditingLabelName('');
		} catch(e)
		{
			setLabels((previous_labels) => {
				const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
				const current_label_index = next_labels.findIndex(label => `${label.label_id}` === label_id_key);
				if(current_label_index !== -1) next_labels[current_label_index] = {...next_labels[current_label_index], label: previous_label_name};
				return next_labels;
			});
			console.log(e.response);
		} finally
		{
			setIsSavingEditedLabelName(false);
		}
	}

	const saveNewLabel = async () => {
		if(isSavingNewLabel) return;

		const trimmed_label = `${newLabelName}`.trim();
		if(!trimmed_label) return;
		if(!schoolInfo || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id || !setLabels) return;

		setIsSavingNewLabel(true);

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'add', label_id:`new_${Date.now()}`, label:trimmed_label, tag_color:newLabelColor};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const saved_label_id = (response.data && response.data.label_id) ? response.data.label_id : data.label_id;
			const saved_tag_color = (response.data && response.data.tag_color) ? response.data.tag_color : newLabelColor;

			setLabels((previous_labels) => {
				const next_labels = Array.isArray(previous_labels) ? [...previous_labels] : [];
				const existing_label_index = next_labels.findIndex(label => `${label.label_id}` === `${saved_label_id}`);
				const saved_label = {label_id: saved_label_id, label: trimmed_label, tag_color: saved_tag_color};

				if(existing_label_index !== -1) next_labels[existing_label_index] = {...next_labels[existing_label_index], ...saved_label};
				else next_labels.unshift(saved_label);

				return next_labels;
			});

			setIsAddingLabel(false);
			setNewLabelName('');
			setNewLabelColor(DEFAULT_LABEL_COLOR);
		} catch(e)
		{
			console.log(e.response);
		} finally
		{
			setIsSavingNewLabel(false);
		}
	}

	const handeDeleteData = async (data_ids) => {
		if(isDeletingSelected || !Array.isArray(data_ids) || data_ids.length <= 0) return;

		const selected_labels = (Array.isArray(labels) ? labels : []).filter((label_info) => data_ids.includes(`${label_info.label_id}`));
		const selected_label_names = selected_labels.map((label_info) => label_info.label || '(No Name)');

		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<div>
						<p>Do you really want to delete the following teams/labels:</p>
						<div style={{marginLeft:'50px', textAlign:'left'}}>
							{selected_label_names.map((label_name, i) => (
								<div key={i}>{label_name}</div>
							))}
						</div>
						<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
					</div>
				</div>
			)
		}

		const delete_data_bool = await swal(options);
		if(!delete_data_bool) return;

		const delete_feedback_started_at = Date.now();
		const deleting_ids = data_ids.map((id) => `${id}`);
		setDeletingLabelIDs(deleting_ids);
		setIsDeletingSelected(true);

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';

		try {
			await Promise.all(data_ids.map((label_id) => {
				const label_info = (Array.isArray(labels) ? labels : []).find((label) => `${label.label_id}` === `${label_id}`);
				const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'remove', label_id:label_id, label:label_info ? label_info.label : null};
				const myInit = {
					response: true,
					body: JSON.stringify(data),
				};

				return API.post(apiName, url, myInit);
			}));

			const ids_to_delete = new Set(data_ids.map((id) => `${id}`));

			if(setLabels)
			{
				setLabels((previous_labels) => {
					const previous_labels_array = Array.isArray(previous_labels) ? previous_labels : [];
					return previous_labels_array.filter((label_info) => !ids_to_delete.has(`${label_info.label_id}`));
				});
			}

			if(setSections)
			{
				setSections((previous_sections) => {
					const previous_sections_array = Array.isArray(previous_sections) ? previous_sections : [];
					return previous_sections_array.map((section_info) => {
						const section_labels = Array.isArray(section_info.labels) ? section_info.labels : [];
						const filtered_labels = section_labels.filter((label_id) => !ids_to_delete.has(`${label_id}`));
						if(filtered_labels.length === section_labels.length) return section_info;
						return {...section_info, labels: filtered_labels};
					});
				});
			}

			if(setStudents)
			{
				setStudents((previous_students) => {
					const previous_students_array = Array.isArray(previous_students) ? previous_students : [];
					return previous_students_array.map((student_info) => {
						const student_labels = Array.isArray(student_info.student_labels) ? student_info.student_labels : [];
						const filtered_student_labels = student_labels.filter((student_label_info) => !ids_to_delete.has(`${student_label_info && student_label_info.label_id}`));
						if(filtered_student_labels.length === student_labels.length) return student_info;
						return {...student_info, student_labels: filtered_student_labels};
					});
				});
			}

			setToggledLabels((previous_toggled) => {
				const previous_toggled_array = Array.isArray(previous_toggled) ? previous_toggled : [];
				return previous_toggled_array.filter((label_id) => !ids_to_delete.has(`${label_id}`));
			});

			setSelectedStudentsByLabel((previous_selected) => {
				const next_selected = {...previous_selected};
				data_ids.forEach((label_id) => {
					delete next_selected[`${label_id}`];
				});
				return next_selected;
			});

				setSelectedData([]);

				if(data_ids.findIndex((label_id) => `${label_id}` === `${editingLabelID}`) !== -1)
				{
					setEditingLabelID(null);
					setEditingLabelName('');
				}
			} catch(e)
			{
				console.log(e.response);
			} finally
		{
			const min_feedback_duration_ms = 450;
			const elapsed_ms = Date.now() - delete_feedback_started_at;
			if(elapsed_ms < min_feedback_duration_ms)
			{
				await new Promise((resolve) => setTimeout(resolve, min_feedback_duration_ms - elapsed_ms));
			}
			setIsDeletingSelected(false);
			setDeletingLabelIDs([]);
		}
	}

	const buildLabelRows = useCallback(() => {
		const section_data = Array.isArray(sections) ? sections : [];
		const subsection_data = Array.isArray(subsections) ? subsections : [];
		const student_data = Array.isArray(students) ? students : [];
		const course_data = Array.isArray(courses) ? courses : [];
		const teacher_data = Array.isArray(teachers) ? teachers : [];
		const label_data = Array.isArray(labels) ? labels : [];
		const labels_by_id = label_data.reduce((results, temp_label_info) => {
			if(!temp_label_info || !('label_id' in temp_label_info)) return results;
			results[`${temp_label_info.label_id}`] = temp_label_info;
			return results;
		}, {});
		const subsection_ids = new Set(subsection_data.map((subsection_info) => `${subsection_info.subsection_id}`));

		return label_data.reduce((results, label_info) => {
			if(!label_info || !('label_id' in label_info)) return results;

			const label_id = `${label_info.label_id}`;
			const label_name = label_info.label ? label_info.label : '(No Name)';

			const sections_with_label = section_data.reduce((section_results, section_info) => {
				if(!section_info || !Array.isArray(section_info.labels)) return section_results;
				if(section_info.section_type !== '1') return section_results;

				const section_has_label = section_info.labels.findIndex(temp_label_id => `${temp_label_id}` === label_id) !== -1;
				if(!section_has_label) return section_results;

				const course_info = course_data.find(course => `${course.course_id}` === `${section_info.course_id}`);
				const teacher_info = teacher_data.find(teacher => `${teacher.teacher_id}` === `${section_info.teacher_id}`);
				const section_labels = (Array.isArray(section_info.labels) ? section_info.labels : [])
					.map((section_label_id) => labels_by_id[`${section_label_id}`])
					.filter((section_label_info) => section_label_info)
					.sort((a, b) => `${a.label || ''}`.localeCompare(`${b.label || ''}`))
					.map((section_label_info) => ({
						label_id: section_label_info.label_id,
						label: section_label_info.label ? section_label_info.label : '(No Name)',
						tag_color: getLabelColorClass(section_label_info)
					}));

					section_results.push({
						section_id: section_info.section_id,
						section_number: section_info.section_number,
						course_name: course_info ? course_info.name : 'Unknown Course',
						course_code: course_info ? course_info.course_code : '--',
						teacher_name: teacher_info ? `${teacher_info.name}, ${teacher_info.first_name}` : null,
						is_subsection: subsection_ids.has(`${section_info.section_id}`),
						is_inclusion: `${section_info.is_inclusion}` === '1',
						section_labels: section_labels
					});

				return section_results;
			}, []);

			sections_with_label.sort((a, b) => {
				const name_sort = `${a.course_name}`.localeCompare(`${b.course_name}`);
				if(name_sort !== 0) return name_sort;
				return naturalSortCompare(a.section_number || '', b.section_number || '');
			});

			const students_with_label = student_data.reduce((student_results, student_info) => {
				if(!student_info || !Array.isArray(student_info.student_labels)) return student_results;

				const student_has_label = student_info.student_labels.findIndex(student_label => student_label && `${student_label.label_id}` === label_id) !== -1;
				if(!student_has_label) return student_results;

				student_results.push({
					student_id: student_info.student_id,
					first_name: student_info.first_name,
					last_name: student_info.last_name,
					student_code: student_info.student_code,
					grade: student_info.grade
				});
				return student_results;
			}, []);

			students_with_label.sort((a, b) => {
				const last_name_sort = `${a.last_name}`.localeCompare(`${b.last_name}`);
				if(last_name_sort !== 0) return last_name_sort;
				return `${a.first_name}`.localeCompare(`${b.first_name}`);
			});

			results.push({
				label_id: label_info.label_id,
				label: label_name,
				tag_color: getLabelColorClass(label_info),
				num_sections_with_label: sections_with_label.length,
				num_students_with_label: students_with_label.length,
				sections_with_label: sections_with_label,
				students_with_label: students_with_label
			});

			return results;
		}, []);
	}, [labels, sections, subsections, students, courses, teachers]);

	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && isLoading) return;
		const search_value = document.getElementsByClassName("school-data-search-bar")[0].value;

		if(search_value.length > 1)
		{
			const filtered_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['label']);
			setDisplayResults([...filtered_results]);
		}
		else
		{
			setDisplayResults([...filteredResultsRef.current]);
		}

		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [filteredResultsRef, isLoading]);

	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const handleSortClick = (sortedData) => {
		setDisplayResults([...sortedData]);
	}

	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(sectionsLoading || studentsLoading || coursesLoading || teachersLoading) return;
		setFilteredResults([...buildLabelRows()]);
	}, [buildLabelRows, sectionsLoading, studentsLoading, coursesLoading, teachersLoading]);

	useEffect(() => {
		if(!sectionsLoading && !studentsLoading && !coursesLoading && !teachersLoading) setIsLoading(false);
	}, [sectionsLoading, studentsLoading, coursesLoading, teachersLoading]);

	useEffect(() => {
		const label_ids_in_results = new Set(displayResults.map((label_info) => `${label_info.label_id}`));
		setSelectedData((previous_selected) => {
			const previous_selected_array = Array.isArray(previous_selected) ? previous_selected : [];
			const filtered_selected = previous_selected_array.filter((selected_id) => label_ids_in_results.has(`${selected_id}`));
			if(filtered_selected.length === previous_selected_array.length) return previous_selected_array;
			return filtered_selected;
		});
	}, [displayResults]);

	useEffect(() => {
		if(!showEditSectionModal) setCurrentSectionID(null);
	}, [showEditSectionModal]);

	const row_style = {gridTemplateColumns:'35px 3fr 1fr 1fr 30px'};
	const new_label_row_style = {gridTemplateColumns:'35px 3fr 1fr 1fr 56px'};
	const disableAddNew = isAddingLabel || isSavingNewLabel || isDeletingSelected;

	return (
		<div className='school-data-content-screen'>
			{showEditSectionModal &&
				<EditSection schoolInfo={schoolInfo} currentView={null} currentDay={'1'} currentViewType={null} scheduleType={scheduleInfo.schedule_type} sectionType='1' sectionID={currentSectionID} dataID={null} toggleEditSection={toggleEditSection} scheduleInfo={scheduleInfo} scheduleStructureData={scheduleStructureData} lunchInfo={lunchInfo} classroomsLoading={classroomsLoading} classrooms={Array.isArray(classrooms) ? classrooms : []} coursesLoading={coursesLoading} courses={Array.isArray(courses) ? courses : []} setCourses={setCourses || (() => {})} teachersLoading={teachersLoading} teachers={Array.isArray(teachers) ? teachers : []} setTeachers={setTeachers || (() => {})} studentsLoading={studentsLoading} students={Array.isArray(students) ? students : []} setStudents={setStudents || (() => {})} sectionsLoading={sectionsLoading} sections={Array.isArray(sections) ? sections : []} setSections={setSections || (() => {})} subsections={Array.isArray(subsections) ? subsections : []} setSubsections={setSubsections || (() => {})} labels={Array.isArray(labels) ? labels : []} setLabels={setLabels || (() => {})} conflicts={[]} setConflicts={() => {}} setConflictsLoading={() => {}} setMatchPercent={() => {}} setMatchPercentLoading={() => {}} toggleEditStudentSchedule={() => {}} allow_hotlinks={false}/>
			}
			{selectedLabelID &&
				<Portal windowName="Manage Student Labels" windowWidth="900" windowHeight="700" cleanUpFunction={() => setSelectedLabelID(null)}>
					<StudentLabelAssignments
						schoolInfo={schoolInfo}
						students={students}
						setStudents={setStudents}
						labelInfo={(Array.isArray(labels) ? labels : []).find((label) => `${label.label_id}` === `${selectedLabelID}`)}
					/>
				</Portal>
			}
			{bulkRemoveStudentsModalData &&
				<div className='modal'>
					<div className='modal-content'>
						<span className="modal-close-right" onClick={() => setBulkRemoveStudentsModalData(null)}>&times;</span>
						<h1>Remove Students?</h1>
						<p>Do you want to remove these {bulkRemoveStudentsModalData.students.length} students from {bulkRemoveStudentsModalData.label_name}?</p>
						<div className='text-align-left'>
							<div className='modal-example-list'>
								{bulkRemoveStudentsModalData.students.map((student_info, index) => (
									<div className='modal-example-row' key={`${student_info.student_id}-${index}`}>{student_info.last_name}, {student_info.first_name}</div>
								))}
							</div>
						</div>
						<div className='modal-btn-container'>
							<div className='btn gray-btn' onClick={() => setBulkRemoveStudentsModalData(null)}>Cancel</div>
							<div className='btn red-btn' onClick={removeSelectedStudentsFromLabel}>Remove Students</div>
						</div>
					</div>
				</div>
			}
			<h1 className='school-data-main-heading capitalize'>Teams/Labels</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-bar-container'>
					<input className='school-data-search-bar' placeholder="Search for a team/label..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
					<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
				</div>
			</div>
				<div className='school-data-database-display'>
					<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
						<div></div>
						<div className='school-data-database-header-col school-data-label-header-col'>Team/Label <SortToggle handleOnClick={handleSortClick} sortKey='label' sortType='text' passedData={displayResults}/></div>
						<div className='school-data-database-header-col school-data-database-header-col-centered school-data-label-count-header'># Sections <SortToggle handleOnClick={handleSortClick} sortKey='num_sections_with_label' sortType='number' passedData={displayResults}/></div>
						<div className='school-data-label-count-header'># Students</div>
						<div className={`school-data-database-add-new-container ${disableAddNew ? 'disabled' : ''}`} onClick={!disableAddNew ? startAddingLabel : null}>
							<div className='school-data-database-add-new-btn'>
								<FontAwesomeIcon className='white-text' icon={faPlus}/>
							</div>
						</div>
					</div>
					{displayResults.length > 0 &&
						<div className='select-all-row'>
							<div className={`select-all-container ${selectedData.length > 0 && 'blue-text'}`} onClick={!isDeletingSelected ? toggleSelectAll : null}>
								<FontAwesomeIcon icon={selectedData.length > 0 ? faMinusSquare : farSquare}/>
								<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
							</div>
							{selectedData.length > 0 &&
								<div className='small-text'>
									<div className='tooltip' data-tooltip-text='Delete selected teams/labels' onClick={!isDeletingSelected ? () => handeDeleteData(selectedData) : null}>
										<div className='red-link'>{`Delete ${selectedData.length} selected`}</div>
									</div>
								</div>
							}
						</div>
					}
					{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
						{displayResults.length === 0 && !isAddingLabel &&
							<div className='school-data-database-message-container'>
								<h4>Sorry, no results were found!</h4>
							</div>
						}
							{isAddingLabel &&
								<div className='school-data-database-row school-data-label-new-row'>
									{isSavingNewLabel ?
										(
											<div className='school-data-database-message-container school-data-label-add-row-loading-container'>
												<img src={require('../../../images/balls.gif')} alt='saving...' style={{height:'35px'}}/>
											</div>
										):
										(
											<div className='upload-screen-student-request-inner-row' style={new_label_row_style}>
												<div></div>
												<div className='school-data-label-new-name-cell'>
													<div style={{display:'grid', gridTemplateColumns:'auto 1fr', alignItems:'center', columnGap:'12px'}}>
														<LabelColorPicker
															colorClass={newLabelColor}
															options={LABEL_COLOR_OPTIONS}
															disabled={isSavingNewLabel}
															onChange={(new_color) => setNewLabelColor(new_color)}
														/>
														<input
															className='school-data-label-new-input'
															value={newLabelName}
															placeholder='Add label name...'
															onChange={(e) => setNewLabelName(e.target.value)}
															onKeyDown={(e) => {
																if(e.key === 'Enter') saveNewLabel();
																if(e.key === 'Escape') cancelAddingLabel();
															}}
															autoFocus={true}
															disabled={isSavingNewLabel}
														/>
													</div>
												</div>
											<div className='text-align-center'>--</div>
											<div className='text-align-center'>--</div>
											<div className='school-data-label-new-actions'>
												<FontAwesomeIcon className={`${isSavingNewLabel ? 'dark-gray-text' : 'green-link'}`} icon={faCheck} onClick={!isSavingNewLabel ? saveNewLabel : null}/>
												<FontAwesomeIcon className={`${isSavingNewLabel ? 'dark-gray-text' : 'cancel-link'}`} icon={faTimes} onClick={!isSavingNewLabel ? cancelAddingLabel : null}/>
											</div>
										</div>
										)
									}
								</div>
							}
						{displayResults.map((label_info, index) => {
						const label_id = label_info.label_id;
						const label_deleting = deletingLabelIDs.includes(`${label_id}`);
						const label_toggled = toggledLabels.findIndex(label => `${label}` === `${label_id}`) !== -1;
						const sections_with_label = label_info.sections_with_label || [];
						const students_with_label = label_info.students_with_label || [];
						const selected_students_raw = Array.isArray(selectedStudentsByLabel[`${label_id}`]) ? selectedStudentsByLabel[`${label_id}`] : [];
						const selected_students = selected_students_raw.filter(student_id => students_with_label.findIndex(student_info => `${student_info.student_id}` === `${student_id}`) !== -1);
						const students_selected = selected_students.length > 0;
						const students_removing = removingStudentsByLabel.includes(`${label_id}`);
						if(label_deleting)
						{
							return (
								<div className='school-data-database-row school-data-label-row-deleting' key={label_id ? label_id : index}>
									<div className='school-data-label-row-full-loader'>
										<img src={require('../../../images/balls.gif')} alt='deleting...' style={{height:'35px'}}/>
									</div>
								</div>
							);
						}

							return (
								<div className='school-data-database-row' key={label_id ? label_id : index}>
										<div className='upload-screen-student-request-inner-row' style={row_style}>
											<FontAwesomeIcon className={selectedData.includes(`${label_id}`) ? 'blue-text' : 'dark-gray-text'} icon={selectedData.includes(`${label_id}`) ? faCheckSquare : farSquare} onClick={!isDeletingSelected ? () => toggleSelectData(label_id) : null}/>
											<div style={{display:'grid', gridTemplateColumns:'auto 1fr', alignItems:'center', columnGap:'12px'}}>
												<LabelColorPicker
													colorClass={getLabelColorClass(label_info)}
													options={isInclusionLabel(label_info) ? [{value:'yellow', display:'Inclusion Yellow'}] : LABEL_COLOR_OPTIONS}
													disabled={isInclusionLabel(label_info)}
													onChange={(new_color) => updateLabelColor(label_id, new_color)}
												/>
												{`${editingLabelID}` === `${label_id}` ?
													(
														<div className='school-data-label-name-editing'>
															<input
																className='school-data-label-new-input school-data-label-editing-input'
																value={editingLabelName}
																placeholder='Update team/label name...'
																onChange={(e) => setEditingLabelName(e.target.value)}
																onKeyDown={(e) => {
																	if(e.key === 'Enter') saveEditedLabelName(label_id);
																	if(e.key === 'Escape') cancelEditingLabelName();
																}}
																autoFocus={true}
																disabled={isSavingEditedLabelName}
															/>
															<FontAwesomeIcon className={`${isSavingEditedLabelName ? 'dark-gray-text' : 'green-link'}`} icon={faCheck} onClick={!isSavingEditedLabelName ? () => saveEditedLabelName(label_id) : null}/>
															<FontAwesomeIcon className={`${isSavingEditedLabelName ? 'dark-gray-text' : 'cancel-link'}`} icon={faTimes} onClick={!isSavingEditedLabelName ? cancelEditingLabelName : null}/>
														</div>
													):
													(
														<div className='school-data-label-editable-name' title='Click to edit' onClick={() => startEditingLabelName(label_info)}>{label_info.label}</div>
													)
												}
											</div>
										<div className='text-align-center'>{label_info.num_sections_with_label}</div>
									<div className='text-align-center'>{label_info.num_students_with_label}</div>
									<FontAwesomeIcon className='gray-link school-data-chevron-right' icon={label_toggled ? faChevronUp : faChevronDown} onClick={() => toggleLabel(label_id)}/>
								</div>
								{label_toggled &&
									<div style={{padding:'10px 30px'}}>
										<div className='upload-screen-request-list'>
											<div className='upload-screen-request-list-inner'>
												<div style={{ display: 'flex', alignItems:'center'}}>
													<div style={{ flex: '1 1' }}>
														<div className='medium-text'><strong>Sections With This Team/Label</strong></div>
													</div>
													<div>
														<div className='btn blue-btn btn-small school-data-student-request-btn' onClick={() => openAddSectionModal(label_id, label_info.label)}>Add Section</div>
													</div>
												</div>
												{sections_with_label.length > 0 ?
													(
																<div className='school-data-label-section-cards'>
																	{sections_with_label.map((section_info, i) => {
																		const section_type_text = (section_info.is_inclusion && section_info.is_subsection) ? 'Inclusion Subsection' : (section_info.is_inclusion) ? 'Inclusion' : (section_info.is_subsection) ? 'Subsection' : null;
																		const section_type_class = (section_info.is_inclusion) ? 'orange-text' : (section_info.is_subsection ? 'blue-text' : '');

																		return (
																		<div className='school-data-label-section-card school-data-label-section-card-sections' key={`${section_info.section_id}-${i}`}>
																			<div className='school-data-label-section-card-info'>
																				{section_type_text &&
																					<div className='small-text school-data-label-section-card-type'>
																						<span className={section_type_class}>{section_type_text}</span>
																					</div>
																				}
																				<div className='school-data-label-section-card-title'>{section_info.course_name} ({section_info.course_code}) - Section {section_info.section_number ? section_info.section_number : '--'}</div>
																				<div className='small-text dark-gray-text school-data-label-section-card-teacher'>{section_info.teacher_name ? section_info.teacher_name : 'No Teacher Assigned'}</div>
																			</div>
																			<div className='school-data-label-section-card-labels'>
																				{(Array.isArray(section_info.section_labels) && section_info.section_labels.length > 0) ?
																					(
																						<>
																						{section_info.section_labels.map((section_label_info) => {
																							const is_current_label = `${section_label_info.label_id}` === `${label_id}`;
																							const section_removing = removingSectionKeys.includes(`${label_id}-${section_info.section_id}`);

																							return (
																								<div className='create-labels-section-label school-data-label-section-chip' key={`${section_info.section_id}-${section_label_info.label_id}`}>
																									<div className={`create-labels-icon ${section_label_info.tag_color}`}></div>
																									<div>{section_label_info.label}</div>
																									{is_current_label &&
																										<>
																										{section_removing ?
																											(
																												<img src={require('../../../images/balls.gif')} alt='removing...' style={{height:'18px'}}/>
																											):
																											(
																												<FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeSectionFromLabel(section_info.section_id, label_id)}/>
																											)
																										}
																										</>
																									}
																								</div>
																							);
																						})}
																						</>
																					):
																					(
																						<div className='small-text dark-gray-text'>No teams/labels on this section.</div>
																					)
																				}
																			</div>
																			<div className='school-data-label-section-card-action'>
																				<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditSection(section_info.section_id)}/>
																			</div>
																	</div>
																		);
																	})}
															</div>
													):
													(
														<div className='upload-screen-empty-requests' style={{margin:'15px 0px'}}>No sections have this team/label yet.</div>
													)
												}

												<div style={{display:'flex', margin:'40px 0px 0px 0px', alignItems:'center'}}>
													<div style={{ flex: '1 1' }}>
														<div className='medium-text'><strong>Students With This Team/Label</strong></div>
													</div>
													{students_removing &&
														<div className='loader' style={{height:'24px', width:'24px', marginRight:'10px'}}></div>
													}
													<div>
														<div className='btn blue-btn btn-small school-data-student-request-btn' onClick={() => setSelectedLabelID(label_id)}>Edit Students</div>
													</div>
												</div>
												{students_with_label.length > 0 ?
													(
														<div className='upload-screen-request-list'>
															<div className='upload-screen-request-list-inner'>
																<div className='select-all-row'>
																	<div className={`select-all-container ${students_selected && 'blue-text'} ${students_removing ? 'opaque' : ''}`} onClick={!students_removing ? () => toggleSelectAllStudents(label_id, students_with_label) : null}>
																		<FontAwesomeIcon icon={students_selected ? faMinusSquare : farSquare}/>
																		<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
																	</div>
																	{students_selected &&
																		<div className='select-all-actions-container'>
																			<div className='tooltip' data-tooltip-text={`Remove from ${label_info.label}`} onClick={!students_removing ? () => openBulkRemoveStudentsModal(label_id, label_info.label, students_with_label) : null}>
																				<FontAwesomeIcon className={`select-all-action-btn ${students_removing ? 'disabled' : ''}`} icon={faTrashAlt}/>
																			</div>
																		</div>
																	}
																</div>
																<div className='modal-example-list' style={{backgroundColor:'#fff'}}>
																	{students_with_label.map((student_info, i) => (
																		<div className='student-requests-request-line' key={i}>
																			<FontAwesomeIcon className={`${selected_students.includes(`${student_info.student_id}`) ? 'blue-text' : 'dark-gray-text'} ${students_removing ? '' : 'cursor-pointer'}`} icon={selected_students.includes(`${student_info.student_id}`) ? faCheckSquare : farSquare} onClick={!students_removing ? () => toggleSelectStudent(label_id, student_info.student_id) : null}/>
																			<div>
																				<div>{student_info.last_name}, {student_info.first_name}</div>
																				<div className='small-text dark-gray-text'>{student_info.grade}th grade{student_info.student_code ? ` • ${student_info.student_code}` : ''}</div>
																			</div>
																		</div>
																	))}
																</div>
															</div>
														</div>
													):
													(
														<div className='upload-screen-empty-requests' style={{margin:'15px 0px'}}>No students have this team/label yet.</div>
													)
												}
											</div>
										</div>
									</div>
								}
							</div>
						)
					})}
					</>
				)}
			</div>
		</div>
	);
}
